import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SubscriptionPromo from "../components/SubscriptionPromo";
import { API } from "../config";

export default function Booking() {
  const [searchParams] = useSearchParams();
  const payment = searchParams.get("payment");
  const [kind, setKind] = useState("standard");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [startAt, setStartAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", garments: "", notes: "" });

  useEffect(() => {
    if (payment === "success") setMessage("Payment received. Your fitting is confirmed — you will receive a confirmation email shortly.");
    else if (payment === "canceled") setMessage("Payment was canceled. Your time was released; please book again if you still need it.");
  }, [payment]);

  useEffect(() => {
    if (!date) { setSlots([]); return; }
    let cancelled = false;
    setLoading(true);
    setStartAt("");
    fetch(`${API}/availability?date=${encodeURIComponent(date)}&kind=${kind}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setSlots(d.slots || []); })
      .catch(() => { if (!cancelled) setMessage("Unable to load availability. Please try again."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, kind]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const res = await fetch(`${API}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, kind, date, startAt }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || "Unable to submit appointment");
      if (data.url) { window.location.assign(data.url); return; }
      setMessage(data.message || "Request submitted.");
      setSlots([]);
      setStartAt("");
    } catch (err) {
      setMessage(err.message || "Unable to submit appointment");
    } finally {
      setBusy(false);
    }
  }

  const fmt = (d) => {
    const p = new Intl.DateTimeFormat("en-US", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
    const get = (k) => p.find((x) => x.type === k)?.value || "";
    return `${get("year")}-${get("month")}-${get("day")}`;
  };
  const min = fmt(new Date(Date.now() + 86400000));
  const max = fmt(new Date(Date.now() + 89 * 86400000));

  return (
    <main className="shell inner">
      <p className="eyebrow">PRIVATE MOBILE FITTINGS</p>
      <h1 className="page-title">Book Your Tailor</h1>
      <p className="lead darklead">Choose your fitting time, tell us where to meet, and secure your appointment. Your tailor comes to you.</p>
      <div className="booking-layout">
        <div>
          <form className="booking-form" onSubmit={submit} data-testid="booking-form">
            <div className="kind-switch">
              <button type="button" className={kind === "standard" ? "selected" : ""} onClick={() => { setKind("standard"); setMessage(""); }} data-testid="kind-standard">
                <strong>Private Mobile Fitting</strong>
                <span>$100 reservation · Credited toward eligible alterations</span>
              </button>
              <button type="button" className={kind === "after_hours" ? "selected" : ""} onClick={() => { setKind("after_hours"); setMessage(""); }} data-testid="kind-afterhours">
                <strong>After-Hours Request</strong>
                <span>$150 upon approval · Includes $50 convenience fee</span>
              </button>
            </div>
            <div className="form-grid">
              <label>Preferred date
                <input required type="date" min={min} max={max} value={date} onChange={(e) => setDate(e.target.value)} data-testid="booking-date" />
              </label>
              <label>Available time
                <select required value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="booking-slot">
                  <option value="">{loading ? "Loading…" : "Select an available time"}</option>
                  {slots.map((s) => <option key={s.startAt} value={s.startAt}>{s.label} Central</option>)}
                </select>
              </label>
              <label>Full name
                <input required autoComplete="name" value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" />
              </label>
              <label>Email address
                <input required type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" />
              </label>
              <label>Phone number
                <input required type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" />
              </label>
              <label>Fitting location (street, city, state)
                <input required autoComplete="street-address" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="booking-location" />
              </label>
              <label className="full">Garments / alterations needed
                <textarea rows={3} value={form.garments} onChange={(e) => setForm({ ...form, garments: e.target.value })} placeholder="For example: two trouser hems and jacket sleeve adjustments" data-testid="booking-garments" />
              </label>
              <label className="full">Additional notes
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Access instructions, preferred contact method, etc." data-testid="booking-notes" />
              </label>
            </div>
            <p className="fine">
              {kind === "standard"
                ? "A time is held briefly while you complete secure payment. Confirmation follows successful payment."
                : "After-hours is a request, not a confirmed booking. We review your request, then send a secure $150 payment link if approved."}
              {" "}All appointments are in Central Time.
            </p>
            <button className="button gold" disabled={busy || !startAt} data-testid="booking-submit">
              {busy ? "Processing…" : kind === "standard" ? "Continue to Secure $100 Payment" : "Request After-Hours Appointment"}
            </button>
            {message && <p role="status" className={"form-message" + (message.toLowerCase().includes("canceled") || message.toLowerCase().includes("unable") || message.toLowerCase().includes("taken") ? " error" : "")} data-testid="booking-message">{message}</p>}
          </form>
        </div>
        <aside className="booking-aside">
          <h3>Before you book</h3>
          <p><strong>Standard:</strong> Monday–Tuesday, 10 AM–5 PM; Saturday, 10 AM–3 PM.</p>
          <p><strong>After-hours:</strong> Monday–Tuesday, 5–8 PM, by approval.</p>
          <p><strong>Fitting reservation:</strong> $100 applied to eligible alteration labor.</p>
          <p><strong>After-hours:</strong> $150 total, including a $50 noncreditable convenience fee.</p>
          <p><strong>Service:</strong> Blue Springs and greater Kansas City. Travel supplements, if any, are confirmed before your visit.</p>
          <p className="fine">A garment estimate is provided at fitting; the reservation is not a fixed price for alteration work.</p>
        </aside>
      </div>
      <section className="section"><SubscriptionPromo compact /></section>
    </main>
  );
}
